import _ from 'underscore';
import MobileDetect from 'mobile-detect';

// var googleMaps = window.google.maps;

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  new MobileDetect(window.navigator.userAgent).mobile();

const DEFAULT_CONFIG = googleMaps => ({
  zoom: 14,
  disableDefaultUI: true,
  disableDoubleClickZoom: false,
  mapTypeControl: false,
  fullscreenControlOptions: {
    position: googleMaps.ControlPosition.RIGHT_BOTTOM
  },
  zoomControl: !isMobileDevice(),
  zoomControlOptions: {
    position: google.maps.ControlPosition.LEFT_BOTTOM
  },
  minZoom: 5,
  draggable: true,
  scrollwheel: true,
  gestureHandling: 'greedy'
});

const MapsAPI = function(mapId, options) {
  this.options = options || {};
  this.markers = {};
  this.circles = {};
  this.mapId = mapId;
  this.googleMaps = window.google.maps;

  this.map = new this.googleMaps.Map(
    document.getElementById(mapId),
    _.defaults(this.options, DEFAULT_CONFIG(this.googleMaps))
  );

  if (this.options.transitLayer) {
    const tL = new this.googleMaps.TransitLayer();
    tL.setMap(this.map);
  }
};

MapsAPI.prototype.addMarker = _addMarker;
MapsAPI.prototype.removeMarker = _removeMarker;
MapsAPI.prototype.isMarkerDefined = _isMarkerDefined;
MapsAPI.prototype.removeAllMarkers = _removeAllMarkers;
MapsAPI.prototype.changeMarkerIcon = _changeMarkerIcon;
MapsAPI.prototype.changeMarkerLabel = _changeMarkerLabel;
MapsAPI.prototype.getMarkerLabel = _getMarkerLabel;
MapsAPI.prototype.changeMarkerZIndex = _changeMarkerZIndex;
MapsAPI.prototype.setMarkerBounceAnimation = _setMarkerBounceAnimation;
MapsAPI.prototype.replaceMarkerListener = _replaceMarkerListener;
MapsAPI.prototype.clearMarkerListeners = _clearMarkerListeners;
MapsAPI.prototype.addMapListener = _addMapListener;
MapsAPI.prototype.removeMapListeners = removeMapListeners;
MapsAPI.prototype.addCircle = _addCircle;
MapsAPI.prototype.removeCircle = _removeCircle;
MapsAPI.prototype.setCenter = _setCenter;
MapsAPI.prototype.fitBoundsToMarkers = _fitBoundsToMarkers;
MapsAPI.prototype.fitBoundsToCoords = _fitBoundsToCoords;
MapsAPI.prototype.increaseZoom = _increaseZoom;
MapsAPI.prototype.setZoom = _setZoom;
MapsAPI.prototype.getBounds = _getBounds;
MapsAPI.prototype.coordsInBounds = _coordsInBounds;

function _addMarker(markerId, opts) {
  if (this.markers[markerId]) {
    this.removeMarker(markerId);
  }

  const markerOpts = {
    position: {
      lat: parseFloat(opts.lat),
      lng: parseFloat(opts.lng)
    },
    map: this.map,
    icon: opts.icon,
    label: opts.label
  };

  if (opts.zIndex) {
    markerOpts.zIndex = opts.zIndex;
  }

  this.markers[markerId] = new this.googleMaps.Marker(markerOpts);
}

function _removeMarker(markerId) {
  if (this.markers[markerId]) {
    google.maps.event.clearInstanceListeners(this.markers[markerId]);
    this.markers[markerId].setMap(null);
    this.markers[markerId] = null;
  }
}

function _isMarkerDefined(markerId) {
  return !!this.markers[markerId];
}

function _removeAllMarkers() {
  for (const markerId in this.markers) {
    if (this.markers.hasOwnProperty(markerId)) {
      this.removeMarker(markerId);
    }
  }
}

function _changeMarkerIcon(markerId, iconPath) {
  if (this.markers[markerId]) {
    this.markers[markerId].setIcon(iconPath);
  }
}

function _changeMarkerLabel(markerId, label) {
  if (this.markers[markerId]) {
    this.markers[markerId].setLabel(label);
  }
}

function _getMarkerLabel(markerId) {
  if (this.markers[markerId]) {
    return this.markers[markerId].getLabel();
  }

  return null;
}

function _changeMarkerZIndex(markerId, zIndex) {
  if (this.markers[markerId]) {
    this.markers[markerId].setZIndex(zIndex);
  }
}

function _setMarkerBounceAnimation(markerId, timeout) {
  if (this.markers[markerId]) {
    this.markers[markerId].setAnimation(this.googleMaps.Animation.BOUNCE);

    if (timeout) {
      setTimeout(() => {
        if (this.markers[markerId]) {
          this.markers[markerId].setAnimation(null);
        }
      }, timeout);
    }
  }
}

function _replaceMarkerListener(markerId, eventName, callback) {
  if (this.markers[markerId]) {
    this.googleMaps.event.clearListeners(this.markers[markerId], eventName);
    this.googleMaps.event.addListener(
      this.markers[markerId],
      eventName,
      callback
    );
  }
}

function _clearMarkerListeners(markerId, eventName) {
  if (this.markers[markerId]) {
    this.googleMaps.event.clearListeners(this.markers[markerId], eventName);
  }
}

function _addMapListener(eventName, callback) {
  this.map.addListener(eventName, callback);
}

function removeMapListeners() {
  this.googleMaps.event.clearInstanceListeners(this.map);
}

function _addCircle(circleId, opts) {
  if (this.circles[circleId]) {
    this.removeCircle(circleId);
  }

  this.circles[circleId] = new this.googleMaps.Circle({
    strokeColor: opts.color,
    strokeOpacity: 0.4,
    strokeWeight: 1,
    fillColor: opts.color,
    fillOpacity: 0.15,
    map: this.map,
    center: {
      lat: parseFloat(opts.lat),
      lng: parseFloat(opts.lng)
    },
    radius: opts.radius
  });
}

function _removeCircle(circleId) {
  if (this.circles[circleId]) {
    this.circles[circleId].setMap(null);
    this.circles[circleId] = null;
  }
}

function _setCenter(opts) {
  this.map.setCenter({
    lat: parseFloat(opts.lat),
    lng: parseFloat(opts.lng)
  });
}

function _fitBoundsToMarkers() {
  const bounds = new this.googleMaps.LatLngBounds();
  _.each(this.markers, marker => {
    bounds.extend(marker.getPosition());
  });
  this.map.fitBounds(bounds);
}

function _fitBoundsToCoords(markersCoords) {
  const bounds = new this.googleMaps.LatLngBounds();
  if (markersCoords) {
    _.each(markersCoords, coords => {
      if (coords.lat && coords.lng) {
        bounds.extend(new this.googleMaps.LatLng(coords.lat, coords.lng));
      }
    });
  }
  this.map.fitBounds(bounds);
}

function _increaseZoom() {
  const currentZoom = this.map.getZoom();
  this.map.setZoom(currentZoom + 1);
}

function _setZoom(zoomValue) {
  this.map.setZoom(zoomValue);
}

function _getBounds() {
  const bounds = this.map.getBounds();

  return bounds;
}

function _coordsInBounds(coords, mapPadding) {
  const bounds = this.map.getBounds();
  const latlng = new this.googleMaps.LatLng(coords.lat, coords.lng);

  if (bounds && bounds.contains(latlng)) {
    if (!mapPadding) {
      return true;
    }

    const mapDiv = document.getElementById(this.mapId);
    if (!mapDiv) {
      return true;
    }
    const mapWidth = mapDiv.offsetWidth;
    const mapHeight = mapDiv.offsetHeight;

    const scale = Math.pow(2, this.map.getZoom());

    const projection = this.map.getProjection();
    const coordsPoint = projection.fromLatLngToPoint(latlng);
    const norhtWestPoint = projection.fromLatLngToPoint(
      new google.maps.LatLng(
        bounds.getNorthEast().lat(),
        bounds.getSouthWest().lng()
      )
    );

    const xOffset = Math.floor((coordsPoint.x - norhtWestPoint.x) * scale);
    const yOffset = Math.floor((coordsPoint.y - norhtWestPoint.y) * scale);

    const inXLimits =
      xOffset - mapPadding > 0 && xOffset + mapPadding < mapWidth;
    const inYLimits =
      yOffset - mapPadding > 0 && yOffset + mapPadding < mapHeight;
    if (inXLimits && inYLimits) {
      return true;
    }
  }

  return false;
}

export default MapsAPI;
